import { FC } from 'react';

/**
 * Modal decoration configuration
 */
export interface ModalDecoration {
	emoji: string;
	top?: string;
	right?: string;
	bottom?: string;
	left?: string;
	fontSize: string;
	opacity: number;
	rotation: number;
}

/**
 * A single semantic token value with light and dark mode variants.
 * Maps directly to Chakra's semantic token format.
 */
interface TokenValue {
	default: string;
	_dark: string;
}

/**
 * Semantic token overrides for seasonal themes.
 *
 * Each token provides { default, _dark } values. At render time,
 * resolveTokenOverrides() picks the correct value based on the
 * current hour and injects it as a CSS variable override.
 *
 * Only tokens that differ from the base/company theme need to be
 * listed here (primarily accent colors). All other semantic tokens
 * resolve natively through Chakra's data-theme attribute.
 */
export interface SeasonalTokenOverrides {
	colors: {
		primary: {
			fg: TokenValue;
			fgStrong: TokenValue;
			fgStronger: TokenValue;
			fgStrongest: TokenValue;
			bg: TokenValue;
			bgHover: TokenValue;
			bgActive: TokenValue;
			bgAlpha: TokenValue;
			bgAlphaHover: TokenValue;
			bgAlphaActive: TokenValue;
		};
		bg: {
			canvas: TokenValue;
			surface: TokenValue;
			soft: TokenValue;
			subtle: TokenValue;
			topNav: TokenValue;
			inset: TokenValue;
		};
		text: {
			interactive: TokenValue;
		};
		border: {
			default: TokenValue;
			soft: TokenValue;
		};
	};
}

/**
 * Helper to create a TokenValue where both modes use the same value.
 * Useful for force-dark themes (fall/winter) where a single palette applies.
 */
export const sameMode = (value: string): TokenValue => ({ default: value, _dark: value });

/**
 * Helper to create a TokenValue with distinct light and dark values.
 */
export const lightDark = (light: string, dark: string): TokenValue => ({ default: light, _dark: dark });

/**
 * Modal color configuration
 */
export interface ModalColors {
	overlayBg: string;
	contentBg: string;
	contentBgImage?: string;
	contentBgSize?: string;
	contentBgPosition?: string;
	contentBgRepeat?: string;
	borderColor: string;
	boxShadow: string;
	logoDropShadowColor: string;
	subtitleColor: string;
	enableButtonBg: string;
	enableButtonHoverBg: string;
	enableButtonActiveBg: string;
	enableButtonBoxShadow: string;
	enableButtonHoverBoxShadow: string;
	enableButtonTextColor?: string;
	cancelButtonColor: string;
	cancelButtonHoverColor: string;
	noticeBg: string;
	noticeBorderColor: string;
	noticeTextColor: string;
	logoOverride?: 'light' | 'dark';
	logoMaxWidth?: string;
	featureCardBg?: string;
	featureCardBorderColor?: string;
	featureCardTextColor?: string;
	closeButtonHoverBg?: string;
}

/**
 * Modal feature description
 */
export interface ModalFeature {
	emoji: string;
	text: string;
}

/**
 * Modal text content configuration
 */
export interface ModalText {
	subtitle: string;
	features: [ModalFeature, ModalFeature, ModalFeature];
	endDateNotice: string;
	enableButtonText: string;
	cancelButtonText: string;
}

/**
 * Color mode strategy for seasonal themes.
 * - 'force-dark': Always force dark mode (existing behavior for fall/winter)
 * - 'time-based': Use current time of day to pick light or dark mode
 */
export type ColorModeStrategy = 'force-dark' | 'time-based';

/**
 * Complete configuration for a seasonal theme
 */
export interface SeasonConfig {
	themeClassName: string;
	toggleEmoji: string;

	logoLight: string;
	logoDark: string;

	modalSound: string;
	modalSoundVolume: number;

	/**
	 * Chakra semantic token overrides with { default, _dark } values.
	 * Passed to createTheme() and applied via a nested ChakraProvider.
	 */
	semanticTokenOverrides: SeasonalTokenOverrides;

	colorModeStrategy?: ColorModeStrategy;

	buttonTexts: string[];
	modalDecorations: ModalDecoration[];
	modalColors: ModalColors;
	modalText: ModalText;
	EffectsComponent: FC;
	avatarOverlayImage?: string;
	/** Width/height multiplier vs avatar box; default 1.4 when omitted (winter) */
	avatarOverlayScale?: number;
	/** Nudge overlay vertically, e.g. `6px` down, when artwork is asymmetric (spring wreath) */
	avatarOverlayTranslateY?: string;
}
